import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Gift, Flame, Wallet, Check, CheckCircle2, Clock, Sparkles, Star } from 'lucide-react';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import PageContainer from '../PageContainer/PageContainer';
import rewardsService, { RewardCardDto, WalletResponse } from '../../services/rewardsService';
import { drawScratchSurface } from '../../hooks/useScratchCanvas';
import './RewardsWallet.css';

const FLOW_META: Record<string, { label: string; Icon: React.ElementType; tint: string; bg: string }> = {
  direct_scratch:  { label: 'Next Visit',    Icon: Gift,   tint: '#D9472B', bg: '#FFF0EE' },
  milestone_stamp: { label: 'Stamp Reward',  Icon: Flame,  tint: '#B8860B', bg: '#FFF8E1' },
  milestone_spend: { label: 'Spend Reward',  Icon: Wallet, tint: '#7C5C00', bg: '#F5F0E4' },
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

/** Today as YYYY-MM-DD in India time — card validity dates are IST calendar days. */
function todayIst() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

// ---------------------------------------------------------------------------
// Scratch overlay canvas
// ---------------------------------------------------------------------------
function ScratchOverlay({ onScratched }: { onScratched: () => void }) {
  const canvasRef  = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDrawing  = useRef(false);
  const revealed   = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    const canvas    = canvasRef.current;
    if (!container || !canvas) return;
    function resize() {
      const w = container!.clientWidth;
      const h = container!.clientHeight;
      if (w > 0 && h > 0) drawScratchSurface(canvas!, w, h);
    }
    resize();
    const obs = new ResizeObserver(resize);
    obs.observe(container);
    return () => obs.disconnect();
  }, []);

  function scratchAt(cx: number, cy: number) {
    const canvas = canvasRef.current;
    if (!canvas || revealed.current) return;
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc((cx - rect.left) * sx, (cy - rect.top) * sy, 20 * sx, 0, Math.PI * 2);
    ctx.fill();
    const d = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    let cleared = 0;
    for (let i = 3; i < d.length; i += 80) if (d[i] === 0) cleared++;
    if (cleared / (d.length / 80) > 0.55 && !revealed.current) {
      revealed.current = true;
      onScratched();
    }
  }

  return (
    <div ref={containerRef} style={{ position: 'absolute', inset: 0, zIndex: 2 }}>
      <canvas
        ref={canvasRef}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', touchAction: 'none', cursor: 'crosshair', borderRadius: 18 }}
        onMouseDown={(e)  => { isDrawing.current = true; scratchAt(e.clientX, e.clientY); }}
        onMouseUp={()     => { isDrawing.current = false; }}
        onMouseLeave={()  => { isDrawing.current = false; }}
        onMouseMove={(e)  => isDrawing.current && scratchAt(e.clientX, e.clientY)}
        onTouchStart={(e) => { e.preventDefault(); scratchAt(e.touches[0].clientX, e.touches[0].clientY); }}
        onTouchMove={(e)  => { e.preventDefault(); scratchAt(e.touches[0].clientX, e.touches[0].clientY); }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single scratch card
// ---------------------------------------------------------------------------
function ScratchCard({
  card, onScratch, celebrating,
}: {
  card: RewardCardDto;
  onScratch: (id: number) => Promise<string | null>;
  celebrating: boolean;
}) {
  const meta      = FLOW_META[card.flowType] ?? FLOW_META['direct_scratch'];
  const { Icon, label, tint, bg } = meta;
  const isLocked  = card.state === 'locked';
  const isScratched = card.state === 'scratched';
  const isRedeemed  = card.state === 'redeemed';
  const isExpired   = card.state === 'expired';
  const dim = isRedeemed || isExpired;
  const [scratching, setScratching] = useState(false);
  const [scratchError, setScratchError] = useState<string | null>(null);
  const notYetValid = isLocked && !!card.validFrom && card.validFrom > todayIst();
  const canScratch  = isLocked && !notYetValid && !scratchError;

  async function handleScratched() {
    setScratching(false);
    setScratchError(await onScratch(card.id));
  }

  const cls = [
    'rewards-card',
    isLocked   ? 'rewards-card--locked'   : '',
    isScratched ? 'rewards-card--scratched' : '',
    dim        ? 'rewards-card--dim'       : '',
  ].filter(Boolean).join(' ');

  return (
    <div style={{ position: 'relative' }}>
      {celebrating && (
        <div style={{ position: 'absolute', top: -8, right: -8, zIndex: 10, pointerEvents: 'none' }}>
          <Sparkles size={20} color="#F5C21A" />
        </div>
      )}
      <button
        className={cls}
        style={isScratched ? { borderColor: tint, background: bg } : undefined}
        onClick={() => canScratch && !scratching && setScratching(true)}
        disabled={!canScratch}
        aria-label={isLocked ? 'Scratch to reveal reward' : card.rewardLabel}
      >
        <div className="rewards-card__inner">
          {/* Type badge row */}
          <div className="rewards-card__type-row">
            <span
              className="rewards-card__type-badge"
              style={
                isLocked
                  ? undefined
                  : { backgroundColor: `${tint}1A`, color: tint }
              }
            >
              {label}
            </span>
            <Icon size={14} color={isLocked ? 'rgba(31,36,40,0.6)' : dim ? '#C2B89A' : tint} />
          </div>

          {/* Locked: scratch prompt */}
          {isLocked && (
            <div className="rewards-card__scratch-hint">
              <div className="rewards-card__coin">🎰</div>
              {notYetValid ? (
                <>
                  <p className="rewards-card__scratch-hint-title">Unlocks on {formatDate(card.validFrom!)}</p>
                  <p className="rewards-card__scratch-hint-sub">Come back then to scratch</p>
                </>
              ) : scratchError ? (
                <>
                  <p className="rewards-card__scratch-hint-title">Couldn't reveal</p>
                  <p className="rewards-card__scratch-hint-sub">{scratchError}</p>
                </>
              ) : (
                <>
                  <p className="rewards-card__scratch-hint-title">Scratch to reveal</p>
                  <p className="rewards-card__scratch-hint-sub">Tap &amp; drag to uncover</p>
                </>
              )}
            </div>
          )}

          {/* Scratched: show reward */}
          {isScratched && (
            <div className="rewards-card__body">
              <p className="rewards-card__reward-label" style={{ color: tint }}>{card.rewardLabel}</p>
              {card.validUntil && (
                <span className="rewards-card__validity">
                  <CheckCircle2 size={11} /> Use by {formatDate(card.validUntil)}
                </span>
              )}
              {card.redemptionCode && (
                <span className="rewards-card__code" style={{ background: `${tint}12`, color: tint }}>
                  {card.redemptionCode}
                </span>
              )}
            </div>
          )}

          {/* Redeemed */}
          {isRedeemed && (
            <div className="rewards-card__body">
              <p className="rewards-card__status">{card.rewardLabel}</p>
              <span className="rewards-card__status-note"><CheckCircle2 size={11} /> Redeemed</span>
            </div>
          )}

          {/* Expired */}
          {isExpired && (
            <div className="rewards-card__body">
              <p className="rewards-card__status">{card.rewardLabel}</p>
              {card.validUntil && (
                <span className="rewards-card__status-note"><Clock size={11} /> Expired {formatDate(card.validUntil)}</span>
              )}
            </div>
          )}

          {card.orderId && card.orderCode && (
            <span className="rewards-card__order-ref">Order #{card.orderCode}</span>
          )}
        </div>

        {scratching && (
          <ScratchOverlay onScratched={handleScratched} />
        )}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Beautiful stamp collectible card
// ---------------------------------------------------------------------------
function StampCard({
  current, total, restaurantName, logoUrl,
}: {
  current: number;
  total: number;
  restaurantName?: string;
  logoUrl?: string;
}) {
  const [imgError, setImgError] = useState(false);
  const initials = (restaurantName ?? 'R').slice(0, 1).toUpperCase();

  return (
    <div className="rewards-stamp-card">
      {/* Card header: restaurant identity */}
      <div className="rewards-stamp-card__header">
        {logoUrl && !imgError ? (
          <img
            src={logoUrl}
            alt={restaurantName}
            className="rewards-stamp-card__logo"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="rewards-stamp-card__logo-fallback">{initials}</div>
        )}
        <div className="rewards-stamp-card__title">
          <h3>{restaurantName || 'Loyalty Stamps'}</h3>
          <p>Stamp Card</p>
        </div>
        <Flame size={16} color="#F5C21A" />
      </div>

      {/* Stamp grid */}
      <div className="rewards-stamp-card__body">
        <div className="rewards-stamp-grid">
          {Array.from({ length: total }).map((_, i) => {
            const filled = i < current;
            return (
              <div key={i} className={`rewards-stamp rewards-stamp--${filled ? 'filled' : 'empty'}`}>
                {filled ? (
                  <>
                    <div className="rewards-stamp__inner">
                      {logoUrl && !imgError ? (
                        <img src={logoUrl} alt="" onError={() => setImgError(true)} />
                      ) : (
                        <div className="rewards-stamp__fallback">{initials}</div>
                      )}
                    </div>
                    <span className="rewards-stamp__check" aria-hidden>
                      <Check size={9} strokeWidth={3.5} color="#1A1209" />
                    </span>
                  </>
                ) : (
                  <div className="rewards-stamp__inner">
                    <div className="rewards-stamp__dot" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="rewards-stamp-card__footer">
          <span className="rewards-stamp-card__count">{current} / {total} visits</span>
          {current < total ? (
            <span className="rewards-stamp-card__cta">{total - current} more to unlock 🎁</span>
          ) : (
            <span className="rewards-stamp-card__cta" style={{ color: '#4CAF50' }}>Reward ready!</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Spend progress card
// ---------------------------------------------------------------------------
function SpendCard({ current, total }: { current: number; total: number }) {
  const pct = Math.min(100, (current / total) * 100);
  return (
    <div className="rewards-spend-card">
      <div className="rewards-spend-card__header">
        <div className="rewards-spend-card__icon">
          <Wallet size={16} color="#B8860B" />
        </div>
        <p className="rewards-spend-card__title">Spend Milestone</p>
        <Star size={14} color="#B8860B" style={{ marginLeft: 'auto' }} />
      </div>
      <div className="rewards-spend-bar-track">
        <div className="rewards-spend-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="rewards-spend-meta">
        <span className="rewards-spend-meta__spent">₹{current.toLocaleString('en-IN')} spent</span>
        <span className="rewards-spend-meta__target">Goal: ₹{total.toLocaleString('en-IN')}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function RewardsWallet() {
  const { appName } = useParams<{ appName: string }>();
  const [wallet, setWallet]       = useState<WalletResponse | null>(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [celebrate, setCelebrate] = useState<number | null>(null);

  const fetchWallet = useCallback(async (silent = false) => {
    if (!appName) return;
    try {
      if (!silent) setLoading(true);
      const data = await rewardsService.getWallet(appName);
      setWallet(data);
    } catch {
      if (!silent) setError('Could not load your rewards. Please try again.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [appName]);

  useEffect(() => { fetchWallet(); }, [fetchWallet]);

  /** Returns null on success, or a message to show on the card when the server refuses the scratch. */
  async function handleScratch(cardId: number): Promise<string | null> {
    if (!appName) return null;
    try {
      const result = await rewardsService.scratchCard(appName, cardId);
      setWallet((w) => w && {
        ...w,
        cards: w.cards.map((c) => c.id === cardId ? {
          ...c,
          state: 'scratched',
          rewardLabel: result.rewardLabel ?? c.rewardLabel,
          redemptionCode: result.redemptionCode,
          validUntil: result.validUntil ?? c.validUntil,
        } : c),
      });
      setCelebrate(cardId);
      setTimeout(() => setCelebrate(null), 1400);
      fetchWallet(true);
      return null;
    } catch (e: any) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.message;
      // Already scratched elsewhere / expired — the refreshed card state tells the story.
      fetchWallet(true);
      if (status === 401) return 'Please sign in to reveal this reward.';
      return msg || 'Something went wrong. Please try again.';
    }
  }

  const unscratchedCount = useMemo(
    () => wallet?.cards?.filter((c) => c.state === 'locked').length ?? 0,
    [wallet]
  );

  const hasProgress = !!(wallet?.stampProgress || wallet?.spendProgress);
  const hasCards    = !!(wallet?.cards?.length);

  return (
    <div className="rewards-page">
      <Header />
      <PageContainer>
        <div className="rewards-shell">

          {/* Page header */}
          <div className="rewards-page-header">
            <div className="rewards-page-header__left">
              <h1>My Rewards</h1>
              <p>Cards &amp; milestones earned from your orders</p>
            </div>
            {unscratchedCount > 0 && (
              <span className="rewards-new-badge">
                {unscratchedCount} to scratch!
              </span>
            )}
          </div>

          {loading && (
            <p className="rewards-loading">
              <i className="fa fa-spinner fa-spin" aria-hidden /> Loading your rewards…
            </p>
          )}

          {error && <p className="rewards-error">{error}</p>}

          {!loading && wallet && (
            <div className="rewards-body">

              {/* Sidebar — stamps + spend progress */}
              {hasProgress && (
                <aside className="rewards-sidebar">
                  {wallet.stampProgress && (
                    <StampCard
                      current={wallet.stampProgress.current}
                      total={wallet.stampProgress.total}
                      restaurantName={wallet.restaurantName}
                      logoUrl={wallet.restaurantLogoUrl}
                    />
                  )}
                  {wallet.spendProgress && (
                    <SpendCard
                      current={wallet.spendProgress.current}
                      total={wallet.spendProgress.total}
                    />
                  )}
                </aside>
              )}

              {/* Cards grid */}
              <section
                className="rewards-cards-section"
                style={{
                  ...(!hasProgress ? { gridColumn: '1 / -1' } : undefined),
                  ...(!hasCards ? { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', alignSelf: 'center' } : undefined),
                }}
              >
                {hasCards ? (
                  <>
                    <p className="rewards-cards-section__label">Active &amp; Past Cards</p>
                    <div className="rewards-cards-grid">
                      {wallet.cards!.map((card) => (
                        <ScratchCard
                          key={card.id}
                          card={card}
                          onScratch={handleScratch}
                          celebrating={celebrate === card.id}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="rewards-empty">
                    <div className="rewards-empty__icon">🎁</div>
                    <p className="rewards-empty__title">Complete an order to earn your first card.</p>
                    <p className="rewards-empty__sub">
                      Direct rewards, Stamp milestones, and Spend rewards all appear here.
                    </p>
                  </div>
                )}
                <p className="rewards-footer-note">Cards are issued automatically after each completed order.</p>
              </section>

            </div>
          )}

        </div>
      </PageContainer>
      <Footer />
    </div>
  );
}
